import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Laboratory } from './dtos/laboratory';
import { LaboratoryConenctionForm } from './dtos/laboratoryConnectionForm';
import { LaboratoryEmailTemplateForm } from './dtos/laboratoryEmailTemplateForm';

@Injectable({ providedIn: 'root' })
export class LaboratoriesService {

  private laboratoriesUrl = this.baseUrl + '/api/laboratories';

  public constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    @Inject('API_BASE_URL') private baseUrl: string) { }

  public getAllTemplateTypes(): Observable<string[]> {
    return this.http.get<string[]>(this.laboratoriesUrl + '/GetTemplateTypes')
      .pipe(
        catchError(this.handleError<string[]>('getAllTemplateTypes'))
      );
  }

  public getAllBasicInfo(): Observable<Laboratory[]> {
    return this.http.get<Laboratory[]>(this.laboratoriesUrl + '/GetAllBasicInfo')
      .pipe(
        catchError(this.handleError<Laboratory[]>('getAllBasicInfo'))
      );
  }

  public getCurrentUserBasicInfo(): Observable<Laboratory[]> {
    return this.http.get<Laboratory[]>(this.laboratoriesUrl + '/GetCurrentUserBasicInfo')
      .pipe(
        catchError(this.handleError<Laboratory[]>('getCurrentUserBasicInfo'))
      );
  }

  public getLaboratory(id: string): Observable<Laboratory> {
    return this.http.get<Laboratory>(this.laboratoriesUrl + '/Get/' + id)
      .pipe(
        catchError(this.handleError<Laboratory>('get'))
      );
  }

  public addLaboratory(code: string, name: string, regionId: string | undefined): Observable<string> {
    return this.http.post<string>(this.laboratoriesUrl + '/Add', { code, name, regionId })
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<string>('updateLaboratory'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateLaboratory(id: string, code: string, name: string, regionId: string | undefined): Observable<any> {
    return this.http.put(this.laboratoriesUrl + '/Update/' + id, { id, code, name, regionId })
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<Laboratory>('updateLaboratory'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteLaboratory(laboratoryId: string): Observable<any> {
    return this.http.delete(this.laboratoriesUrl + '/Delete/' + laboratoryId)
      .pipe(
        tap(() => this.toastr.success('Deleted Sucessfully')),
        catchError(this.handleError<Laboratory>('deleteLaboratory'))
      );
  }

  public addLaboratoryConnection(payload: LaboratoryConenctionForm): Observable<string> {
    return this.http.post<string>(this.laboratoriesUrl + '/AddConnection', payload)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<string>('addLaboratoryConnection'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateLaboratoryConnection(payload: LaboratoryConenctionForm): Observable<any> {
    return this.http.put(this.laboratoriesUrl + '/UpdateConnection/' + payload.laboratoryId + '/' + payload.connectionId, payload)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<Laboratory>('updateLaboratoryConnection'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteLaboratoryConnection(laboratoryId: string, connectionId: string): Observable<any> {
    return this.http.delete(this.laboratoriesUrl + '/DeleteConnection/' + laboratoryId + '/' + connectionId)
      .pipe(
        tap(() => this.toastr.success('Deleted Sucessfully')),
        catchError(this.handleError<Laboratory>('deleteLaboratoryConnection'))
      );
  }

  public getLaboratoryEmailTemplateFile(laboratoryId: string, templateId: string): void {
    this.http.get(this.laboratoriesUrl + '/GetEmailTemplateFile/' + laboratoryId +
      '/' + templateId, { responseType: 'blob', observe: 'response' })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .subscribe((response: any) => {
        const headers = response.headers;
        const contentDisposition = headers.get('content-disposition').split('filename=')[1].split(';')[0];
        const blob = new Blob([response.body], { type: response.type });
        const link = document.createElement('a');
        link.setAttribute('href', window.URL.createObjectURL(blob));
        link.setAttribute('download', contentDisposition ?? 'defaultFileName.txt');
        link.click();
      },
        catchError(this.handleError<Blob>('fetchPluginCustomerConfigurationFile'))
      );
  }

  public addLaboratoryEmailTemplate(payload: LaboratoryEmailTemplateForm): Observable<string> {
    const formData = new FormData();
    if (payload.mailTemplate) {
      formData.append('mailTemplate', payload.mailTemplate, payload.mailTemplate.name);
    }
    formData.append('name', payload.name);
    formData.append('templateType', payload.templateType);
    formData.append('fromAddress', payload.fromAddress ?? '');
    formData.append('subject', payload.subject ?? '');
    formData.append('contentType', payload.contentType ?? '');
    formData.append('attachmentTypes', payload.attachmentTypes?.length ? payload.attachmentTypes.toString() : '0');
    formData.append('laboratoryId', payload.laboratoryId);
    return this.http.post<string>(this.laboratoriesUrl + '/AddEmailTemplate', formData)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<string>('updateLaboratoryEmailTemplate'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateLaboratoryEmailTemplate(payload: LaboratoryEmailTemplateForm): Observable<any> {
    const formData = new FormData();
    if (payload.mailTemplate) {
      formData.append('mailTemplate', payload.mailTemplate, payload.mailTemplate.name);
    }
    formData.append('name', payload.name);
    formData.append('templateType', payload.templateType);
    formData.append('fromAddress', payload.fromAddress ?? '');
    formData.append('subject', payload.subject ?? '');
    formData.append('contentType', payload.contentType ?? '');
    formData.append('attachmentTypes', payload.attachmentTypes?.length ? payload.attachmentTypes.toString() : '0');
    formData.append('laboratoryId', payload.laboratoryId);
    formData.append('templateId', payload.templateId);
    return this.http.put(this.laboratoriesUrl + '/UpdateEmailTemplate/' + payload.laboratoryId + '/' + payload.templateId, formData)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<FormData>('updateLaboratoryEmailTemplate'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteLaboratoryEmailTemplate(laboratoryId: string, templateId: string): Observable<any> {
    return this.http.delete(this.laboratoriesUrl + '/DeleteEmailTemplate/' + laboratoryId + '/' + templateId)
      .pipe(
        tap(() => this.toastr.success('Deleted Sucessfully')),
        catchError(this.handleError<Laboratory>('deleteLaboratoryEmailTemplate'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  private handleError<T>(operation = 'operation', result?: T | undefined): (err: any) => Observable<T> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (error: any): Observable<T> => {

      console.error('An error occurred', error);
      this.toastr.error(`${operation} failed: ${error.message}`);

      throw error;
    };
  }
}
